'use client'
import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { CircleIcon } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import gsap from 'gsap'

import { cn } from '../../lib/utils'

const radioGroupItemVariants = cva(
  'bg-blend-overlay gradient-to-b from-white-30 to-white-0 rounded-full transition-[color,box-shadow] outline-none hover:cursor-pointer disabled:cursor-not-allowed z-10',
  {
    variants: {
      size: {
        sm: 'size-5 [&_svg]:size-2',
        md: 'size-6 [&_svg]:size-3',
      },
      color: {
        base: 'bg-base-surface-5  border-base-surface-20 hover:border-base-surface-40 active:bg-base-surface-20 active:border-base-surface-40 disabled:bg-disabled-surface-light disabled:border-disabled-surface-dark border-[1.5px] text-base-main-100',
        neutral:
          'bg-neutral-surface-5  border-neutral-surface-20 hover:border-neutral-surface-40 active:bg-neutral-surface-20 active:border-neutral-surface-40 disabled:bg-disabled-surface-light disabled:border-disabled-surface-dark border-[1.5px] text-neutral-main-100',
      },
    },
    defaultVariants: {
      size: 'md',
      color: 'base',
    },
  },
)

type RadioGroupItemVariants = VariantProps<typeof radioGroupItemVariants>

// Context for size and color
const RadioGroupConfigContext = React.createContext<{
  size?: RadioGroupItemVariants['size']
  color?: RadioGroupItemVariants['color']
}>({})

export interface RadioGroupProps
  extends Omit<
    React.ComponentProps<typeof RadioGroupPrimitive.Root>,
    'color' | 'size'
  > {
  className?: string
  size?: 'sm' | 'md'
  color?: 'base' | 'neutral'
}

interface RadioGroupItemProps
  extends Omit<
      React.ComponentProps<typeof RadioGroupPrimitive.Item>,
      keyof RadioGroupItemVariants
    >,
    RadioGroupItemVariants {
  className?: string
}

const radioIndicatorBgStyles: Record<'base' | 'neutral', string> = {
  base: 'bg-base-main',
  neutral: 'bg-neutral-main',
}

function RadioGroup({ className, size, color, ...props }: RadioGroupProps) {
  const groupRef = React.useRef<HTMLDivElement>(null)
  const indicatorRef = React.useRef<HTMLDivElement>(null)
  const [activeItem, setActiveItem] = React.useState<HTMLElement | null>(null)
  const colorKey: 'base' | 'neutral' = color === 'neutral' ? 'neutral' : 'base'
  const indicatorBg = radioIndicatorBgStyles[colorKey]

  // Detect active item changes
  React.useEffect(() => {
    if (!groupRef.current) return
    const updateActive = () => {
      const items = Array.from(
        groupRef.current!.querySelectorAll('[data-slot="radio-group-item"]'),
      )
      const active = items.find(
        (el) => el.getAttribute('data-state') === 'checked',
      ) as HTMLElement | undefined
      setActiveItem(active || null)
    }
    updateActive()
    const observer = new MutationObserver(updateActive)
    observer.observe(groupRef.current, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    })
    return () => observer.disconnect()
  }, [])

  // Animate indicator
  React.useEffect(() => {
    if (!indicatorRef.current || !activeItem || !groupRef.current) return
    const { offsetLeft, offsetWidth, offsetTop, offsetHeight } = activeItem
    const group = groupRef.current
    const isRTL =
      group.dir === 'rtl' || getComputedStyle(group).direction === 'rtl'
    let x = offsetLeft
    if (isRTL) {
      x = group.offsetWidth - (offsetLeft + offsetWidth)
    }
    gsap.to(indicatorRef.current, {
      x,
      y: offsetTop,
      width: offsetWidth,
      height: offsetHeight,
      duration: 0.3,
      ease: 'power2.out',
    })
  }, [activeItem])

  return (
    <RadioGroupConfigContext.Provider value={{ size, color }}>
      <RadioGroupPrimitive.Root
        ref={groupRef}
        data-slot='radio-group'
        className={cn('grid gap-sm  relative', className)}
        {...props}
      >
        {/* Animated indicator */}
        <div
          ref={indicatorRef}
          className={cn(
            'absolute rounded-full z-0 transition-none pointer-events-none',
            indicatorBg,
          )}
          style={{ willChange: 'transform, width, height' }}
        />
        {props.children}
      </RadioGroupPrimitive.Root>
    </RadioGroupConfigContext.Provider>
  )
}

function RadioGroupItem({
  className,
  size: propSize,
  color: propColor,
  ...props
}: RadioGroupItemProps) {
  const context = React.useContext(RadioGroupConfigContext)
  const size = propSize ?? context.size
  const color = propColor ?? context.color
  return (
    <RadioGroupPrimitive.Item
      data-slot='radio-group-item'
      className={cn(radioGroupItemVariants({ size, color }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot='radio-group-indicator'
        className='relative flex items-center justify-center'
      >
        <CircleIcon className='fill-current absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

export { RadioGroup, RadioGroupItem }
